/**
 * Header.jsx - HemaAI V2.0 Navigation Header
 * Fixed for small mobile screens (360px+)
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Droplets, Menu, X, ChevronDown, LogOut,
  Globe, Eye, Sun, Moon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/lib/theme/theme';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const { isDark, toggle } = useTheme();
  const location = useLocation();

  // Clinical tools are login-gated (pending clinical validation), so their nav
  // links only appear once signed in. Public links stay visible to everyone.
  const allNavLinks = [
    { name: 'CBC', path: '/analyzers/cbc', requiresAuth: true },
    { name: 'Anaemia', path: '/analyzers/anemia', requiresAuth: true },
    { name: 'CMP', path: '/analyzers/cmp', requiresAuth: true },
    { name: 'Coagulation', path: '/analyzers/coagulation', requiresAuth: true },
    { name: 'Haemolysis', path: '/analyzers/hemolysis', badge: 'New', requiresAuth: true },
    { name: 'Smear', path: '/analyzers/smear', requiresAuth: true },
    { name: 'Calculators', path: '/calculators', requiresAuth: true },
    { name: 'Trends', path: '/trends', requiresAuth: true },
    { name: 'Units', path: '/tools/converter', requiresAuth: true },
    { name: 'Telemedicine', path: '/telemedicine', requiresAuth: true },
    { name: 'VR Lab', path: '/vr-simulation', badge: 'Beta', icon: Eye, requiresAuth: true },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About', path: '/about-founder' }
  ];
  const navLinks = allNavLinks.filter((l) => !l.requiresAuth || isAuthenticated);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'sw', name: 'Kiswahili', flag: '🇰🇪' }
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    setIsLangOpen(false);
  };

  return (
    <header className="glass-header sticky top-0 z-50">
      {/* Reduced padding: px-2 on mobile, px-4 on sm+ */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        <div className="flex items-center justify-between h-14 sm:h-16">
          
          {/* Logo - Compact on mobile */}
          <Link to="/" className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg sm:rounded-xl flex items-center justify-center">
              <Droplets className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="flex items-baseline">
              <span className="text-lg sm:text-xl font-bold text-gray-900">HemaAI</span>
              <span className="text-xs text-gray-500 ml-1 hidden xs:inline">v2.0</span>
            </div>
          </Link>

          {/* Desktop Navigation - Only on lg+ */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-red-50 text-red-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {link.icon && <link.icon className="w-4 h-4" />}
                {link.name}
                {link.badge && (
                  <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                    link.badge === 'Pro' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {link.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions - Optimized for mobile */}
          <div className="flex items-center gap-1 sm:gap-2 md:gap-3">

            {/* Theme toggle (light default; dark helps the smear/image views) */}
            <button
              onClick={toggle}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-50"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Language Selector - Hidden on very small screens, icon only on mobile */}
            <div className="relative hidden sm:block">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center gap-1 sm:gap-2 px-2 py-1.5 sm:py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden md:inline">{currentLang.code.toUpperCase()}</span>
                <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>

              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 ${
                        i18n.language === lang.code ? 'bg-gray-50 text-red-600' : ''
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Auth Buttons */}
            {isAuthenticated ? (
              <div className="flex items-center gap-1 sm:gap-2">
                <Link
                  to="/dashboard"
                  className="hidden sm:block px-2 sm:px-3 py-1.5 sm:py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Dashboard
                </Link>
                <button
                  onClick={logout}
                  className="flex items-center gap-1 px-2 py-1.5 sm:py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Sign In - Hidden on mobile, shown in hamburger menu instead */}
                <Link
                  to="/login"
                  className="hidden sm:block px-2 sm:px-3 py-1.5 sm:py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Sign In
                </Link>
                {/* Get Started - Hidden on mobile, shown in hamburger menu instead */}
                <Link
                  to="/register"
                  className="hidden md:block px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
                >
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile Menu Button - Always visible on mobile */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-1.5 sm:p-2 text-gray-600 hover:bg-gray-50 rounded-lg flex-shrink-0"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
                    location.pathname === link.path
                      ? 'bg-red-50 text-red-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  {link.name}
                  {link.badge && (
                    <span className={`ml-auto px-2 py-0.5 text-xs rounded-full ${
                      link.badge === 'Pro' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {link.badge}
                    </span>
                  )}
                </Link>
              ))}
              
              {/* Mobile-only: Language selector */}
              <div className="sm:hidden border-t mt-2 pt-2">
                <p className="px-3 py-1 text-xs text-gray-500 font-medium">Language</p>
                <div className="flex flex-wrap gap-2 px-3 py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex items-center gap-1 px-2 py-1 text-sm rounded-lg ${
                        i18n.language === lang.code ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Mobile-only: Auth buttons */}
              {!isAuthenticated && (
                <div className="border-t mt-2 pt-3 flex flex-col gap-2 px-3">
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="w-full text-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMenuOpen(false)}
                    className="w-full text-center px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
                  >
                    Get Started Free
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
